import { List, ListItem } from 'foundations-components/transitional/components/List';
import InsertMenuTypeButton, { InsertMenuUpgradeButton } from './InsertMenuTypeButton';
import Raven from 'raven-js';
import InsertQuotes from '../../commands/quotes/InsertQuotes';
import InsertKnowledgeArticle from '../../commands/knowledgeArticles/InsertKnowledgeArticle';
import InsertSnippet from '../../commands/snippets/InsertSnippet';
import InsertMeetings from '../../commands/meetings/InsertMeetings';
import InsertDocuments from '../../commands/documents/InsertDocuments';
import InsertPaymentLink from '../../commands/paymentLink/InsertPaymentLink';
import InsertInvoices from '../../commands/invoices/InsertInvoices';
import { PaymentsEligibilityStatus } from '../../commands/paymentLink/PaymentLinksApi';
import { useCallback, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { PaymentLinksEligibilityContext } from '../../contexts/PaymentLinksEligibilityContext';
import InsertTemplates from '../../commands/templates/InsertTemplates';
import InlineInsertHeader from './InlineInsertHeader';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getHasAccess = (type, auth, paymentsEligibilityStatus) => {
  if (!auth) {
    return true;
  }
  const {
    scopes
  } = auth.user;
  switch (type) {
    case 'MEETINGS':
      return true;
    case 'TEMPLATES':
      return true;
    case 'KNOWLEDGE_ARTICLE':
      return scopes.includes('service-knowledge-access') ? true : 'inbox-insert-knowledge-article';
    case 'PAYMENT_LINK':
      return scopes.includes('payment-links-read') && paymentsEligibilityStatus === PaymentsEligibilityStatus.ACCOUNT_EXISTS;
    case 'INVOICES':
      return scopes.includes('invoices-read');
    case 'VIDEO_INSERTION':
      return scopes.includes('one-to-one-video');
    case 'SNIPPETS':
      return scopes.includes('snippets-read-access') || scopes.includes('snippets-limited-read-access');
    case 'DOCUMENTS':
      return true;
    case 'QUOTES':
      return scopes.includes('quote-access');
    case 'SIGNATURE':
      return true;
    default:
      Raven.captureMessage('NO_HAS_ACCESS');
      return false;
  }
};
const InsertMenuItems = ({
  auth,
  editorConfig,
  handleSelectType,
  insertTypeOrder,
  paymentsEligibilityStatus
}) => {
  return /*#__PURE__*/_jsx(List, {
    wrapChildren: true,
    "data-test-id": "insert-menu-list",
    children: insertTypeOrder.map(type => {
      const access = getHasAccess(type, auth, paymentsEligibilityStatus);
      if (!access || !editorConfig[type]) {
        return null;
      } else if (typeof access === 'string') {
        return /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(InsertMenuUpgradeButton, {
            type: type,
            upgradeKey: access
          }, type)
        }, type);
      } else {
        return /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(InsertMenuTypeButton, {
            editorConfig: editorConfig,
            type: type,
            onSelectMediaType: handleSelectType
          }, type)
        }, type);
      }
    })
  });
};
export default function InsertMenu({
  editorConfig,
  selectedType,
  handleSelectType,
  insertTypeOrder,
  handleViewInsertMenu,
  handleInsertComplete
}) {
  const auth = useContext(AuthContext);
  const paymentsEligibilityStatus = useContext(PaymentLinksEligibilityContext);
  const resetSelectedType = useCallback(() => handleSelectType(''), [handleSelectType]);
  if (!selectedType) {
    return /*#__PURE__*/_jsx(InsertMenuItems, {
      auth: auth,
      editorConfig: editorConfig,
      handleSelectType: handleSelectType,
      insertTypeOrder: insertTypeOrder,
      paymentsEligibilityStatus: paymentsEligibilityStatus
    });
  }
  if (selectedType === 'VIDEO_INSERTION' || selectedType === 'SIGNATURE') {
    return null;
  }
  const contactVid = editorConfig[selectedType].contactVid;
  const outputType = editorConfig[selectedType].outputType;
  const contactEmail = editorConfig[selectedType].contactEmail;
  const supplementalObjects = editorConfig[selectedType].supplementalObjects;
  const onInsertContent = editorConfig[selectedType].onInsertContent;
  const referrerParam = editorConfig[selectedType].referrerParam;
  const apiWrapper = editorConfig[selectedType].apiWrapper;
  const allowSenderMeetingLink = editorConfig[selectedType].allowSenderMeetingLink;
  switch (selectedType) {
    case 'KNOWLEDGE_ARTICLE':
      return /*#__PURE__*/_jsx(View, {
        paddingBlockStart: "300",
        paddingBlockEnd: "200",
        as: "div",
        children: /*#__PURE__*/_jsx(InsertKnowledgeArticle, {
          onViewInsertMenu: handleViewInsertMenu,
          onInsertComplete: handleInsertComplete,
          useContentContainer: true
        })
      });
    case 'PAYMENT_LINK':
      return /*#__PURE__*/_jsx(View, {
        paddingBlockStart: "300",
        paddingBlockEnd: "200",
        as: "div",
        children: /*#__PURE__*/_jsx(InsertPaymentLink, {
          outputType: outputType,
          onViewInsertMenu: handleViewInsertMenu,
          onInsertComplete: handleInsertComplete,
          useContentContainer: true,
          referrerParam: referrerParam
        })
      });
    case 'INVOICES':
      return /*#__PURE__*/_jsx(View, {
        paddingBlockStart: "300",
        paddingBlockEnd: "200",
        as: "div",
        children: /*#__PURE__*/_jsx(InsertInvoices, {
          outputType: outputType,
          onViewInsertMenu: handleViewInsertMenu,
          onInsertComplete: handleInsertComplete,
          useContentContainer: true
        })
      });
    case 'SNIPPETS':
      return /*#__PURE__*/_jsx(View, {
        paddingBlockStart: "300",
        paddingBlockEnd: "200",
        as: "div",
        children: /*#__PURE__*/_jsx(InsertSnippet, {
          contactVid: contactVid,
          outputType: outputType,
          contactEmail: contactEmail,
          supplementalObjects: supplementalObjects,
          onViewInsertMenu: handleViewInsertMenu,
          onInsertComplete: handleInsertComplete,
          onInsertContent: onInsertContent,
          useContentContainer: true,
          apiWrapper: apiWrapper
        })
      });
    case 'TEMPLATES':
      return /*#__PURE__*/_jsx(View, {
        paddingBlockStart: "300",
        paddingBlockEnd: "200",
        as: "div",
        children: /*#__PURE__*/_jsx(InsertTemplates, {
          contactVid: contactVid,
          outputType: outputType,
          contactEmail: contactEmail,
          supplementalObjects: supplementalObjects,
          onViewInsertMenu: handleViewInsertMenu,
          onInsertComplete: handleInsertComplete,
          onInsertContent: onInsertContent,
          useContentContainer: true
        })
      });
    case 'MEETINGS':
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(InlineInsertHeader, {
          onBackClick: resetSelectedType,
          selectedType: selectedType
        }), /*#__PURE__*/_jsx(View, {
          paddingBlockStart: "300",
          paddingBlockEnd: "200",
          as: "div",
          children: /*#__PURE__*/_jsx(InsertMeetings, {
            outputType: outputType,
            onViewInsertMenu: handleViewInsertMenu,
            onInsertComplete: handleInsertComplete,
            onInsertContent: onInsertContent,
            useContentContainer: true,
            resetSelectedType: resetSelectedType,
            allowSenderMeetingLink: allowSenderMeetingLink
          })
        })]
      });
    case 'DOCUMENTS':
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(InlineInsertHeader, {
          onBackClick: resetSelectedType,
          selectedType: selectedType
        }), /*#__PURE__*/_jsx(View, {
          paddingBlockStart: "300",
          paddingBlockEnd: "200",
          as: "div",
          children: /*#__PURE__*/_jsx(InsertDocuments, {
            outputType: outputType,
            contactEmail: contactEmail,
            onViewInsertMenu: handleViewInsertMenu,
            onInsertComplete: handleInsertComplete,
            onInsertContent: onInsertContent,
            useContentContainer: true,
            resetSelectedType: resetSelectedType
          })
        })]
      });
    case 'QUOTES':
      return /*#__PURE__*/_jsx(View, {
        paddingBlockStart: "300",
        paddingBlockEnd: "200",
        as: "div",
        children: /*#__PURE__*/_jsx(InsertQuotes, {
          contactVid: contactVid,
          outputType: outputType,
          onViewInsertMenu: handleViewInsertMenu,
          onInsertComplete: handleInsertComplete,
          onInsertContent: onInsertContent,
          useContentContainer: true
        })
      });
    default:
      Raven.captureMessage('SELECTED_TYPE_UNKNOWN');
      return null;
  }
}